import { useCallback, useEffect, useRef, useState } from 'react'
import { upgraderGameApi } from '../services/upgraderGameApi'
import useUpdateBlocker from '../../../hooks/useUpdateBlocker'
import { beginUpdateOperation, isUpdateOperationBlocked, prepareUpdateRecovery } from '../../../services/appUpdateService'

const idOf = (item) => String(item?.id)
const sortedMultipliers = (groups) => Object.keys(groups).filter((key) => groups[key].length > 0).sort((a, b) => Number(a) - Number(b))
const preferredTarget = (items, source) => items.find((item) => item.name !== source.name) || items[0]
const SPIN_DURATION_MS = 7300

const useUpgraderGame = () => {
  const [inventory, setInventory] = useState([])
  const [source, setSource] = useState(null)
  const [groups, setGroups] = useState({})
  const [multiplier, setMultiplier] = useState(null)
  const [target, setTarget] = useState(null)
  const [chance, setChance] = useState(null)
  const [loading, setLoading] = useState('inventory')
  const [error, setError] = useState(null)
  const [outcome, setOutcome] = useState(null)
  const [spin, setSpin] = useState(null)
  const versionRef = useRef(0)
  const inFlightRef = useRef(false)
  const spinTimerRef = useRef(null)
  const pendingPrizeRef = useRef(null)
  const mountedRef = useRef(false)
  useUpdateBlocker(() => inFlightRef.current || Boolean(pendingPrizeRef.current) || error === 'request', 'upgrader')

  const loadChance = useCallback(async (item, nextTarget, version) => {
    setTarget(nextTarget)
    setChance(null)
    setLoading('chance')
    setError(null)
    try {
      const result = await upgraderGameApi.getChance(item.id, nextTarget.id)
      if (version !== versionRef.current) return
      if (!Number.isFinite(result.chance) || result.chance < 0 || result.chance > 100) throw new Error('Invalid chance')
      setChance(result.chance)
      setLoading(null)
    } catch (requestError) {
      if (version !== versionRef.current) return
      setLoading(null)
      setError('chance')
    }
  }, [])

  const loadTargets = useCallback(async (item, desiredMultiplier) => {
    const version = ++versionRef.current
    setSource(item)
    setGroups({})
    setTarget(null)
    setChance(null)
    setLoading('targets')
    setError(null)
    try {
      const available = await upgraderGameApi.getTargets(item.id)
      if (version !== versionRef.current) return
      setGroups(available)
      const keys = sortedMultipliers(available)
      const nextMultiplier = keys.includes(String(desiredMultiplier)) ? String(desiredMultiplier) : keys.includes('1.5') ? '1.5' : keys[0] || null
      setMultiplier(nextMultiplier)
      if (nextMultiplier) await loadChance(item, preferredTarget(available[nextMultiplier], item), version)
      else setLoading(null)
    } catch (requestError) {
      if (version !== versionRef.current) return
      setLoading(null)
      setError('targets')
    }
  }, [loadChance])

  const refreshInventory = useCallback(async ({ prize = null, previousIds = null, preferredSourceId = null, desiredMultiplier = null } = {}) => {
    const version = ++versionRef.current
    const recoverUpdate = prepareUpdateRecovery('upgrader')
    setLoading('inventory')
    setChance(null)
    setError(null)
    if (prize) pendingPrizeRef.current = { prize, previousIds, desiredMultiplier }
    try {
      let items = await upgraderGameApi.getInventory()
      if (version !== versionRef.current) return
      let earned = null
      if (prize) {
        const findEarned = (list) => list.find((item) => !previousIds.has(idOf(item)) && (item.name === prize.name || idOf(item) === idOf(prize)))
        earned = findEarned(items)
        if (!earned) {
          await new Promise((resolve) => setTimeout(resolve, 700))
          if (version !== versionRef.current) return
          items = await upgraderGameApi.getInventory()
          if (version !== versionRef.current) return
          earned = findEarned(items)
        }
      }
      setInventory(items)
      pendingPrizeRef.current = prize && !earned ? { prize, previousIds, desiredMultiplier } : null
      if (!pendingPrizeRef.current) recoverUpdate()
      const nextSource = prize ? earned : items.find((item) => idOf(item) === String(preferredSourceId)) || items[0]
      if (!nextSource) {
        setSource(null)
        setGroups({})
        setTarget(null)
        setMultiplier(null)
        setLoading(null)
        if (prize && !earned) setError('prize')
        return
      }
      await loadTargets(nextSource, desiredMultiplier)
    } catch (requestError) {
      if (version !== versionRef.current) return
      setLoading(null)
      setError('inventory')
    }
  }, [loadTargets])

  useEffect(() => {
    mountedRef.current = true
    refreshInventory()
    return () => {
      mountedRef.current = false
      versionRef.current += 1
      clearTimeout(spinTimerRef.current)
    }
  }, [refreshInventory])

  const selectSource = (item) => {
    if ((loading && loading !== 'chance') || inFlightRef.current) return
    pendingPrizeRef.current = null
    setOutcome(null)
    loadTargets(item, multiplier)
  }

  const selectMultiplier = (key) => {
    if ((loading && loading !== 'chance') || inFlightRef.current || !source || !groups[key]?.length) return
    const version = ++versionRef.current
    setMultiplier(key)
    setOutcome(null)
    loadChance(source, preferredTarget(groups[key], source), version)
  }

  const selectTarget = (item) => {
    if ((loading && loading !== 'chance') || inFlightRef.current || !source || !groups[multiplier]?.some((candidate) => idOf(candidate) === idOf(item))) return
    const version = ++versionRef.current
    setOutcome(null)
    loadChance(source, item, version)
  }

  const prepareNextRound = async (win, previousIds, prize, desiredMultiplier) => {
    if (win) pendingPrizeRef.current = { prize, previousIds, desiredMultiplier }
    try {
      let items = await upgraderGameApi.getInventory()
      let earned = null
      if (win) {
        const findEarned = (list) => list.find((item) => !previousIds.has(idOf(item)) && (item.name === prize.name || idOf(item) === idOf(prize)))
        earned = findEarned(items)
        if (!earned) {
          await new Promise((resolve) => setTimeout(resolve, 700))
          items = await upgraderGameApi.getInventory()
          earned = findEarned(items)
        }
      }
      const nextSource = win ? earned : items[0]
      if (!nextSource) return { items, error: win ? 'prize' : null }
      const available = await upgraderGameApi.getTargets(nextSource.id)
      const keys = sortedMultipliers(available)
      const nextMultiplier = keys.includes(String(desiredMultiplier)) ? String(desiredMultiplier) : keys.includes('1.5') ? '1.5' : keys[0] || null
      const nextTarget = nextMultiplier ? preferredTarget(available[nextMultiplier], nextSource) : null
      const nextChance = nextTarget ? (await upgraderGameApi.getChance(nextSource.id, nextTarget.id)).chance : null
      if (nextTarget && (!Number.isFinite(nextChance) || nextChance < 0 || nextChance > 100)) throw new Error('Invalid chance')
      pendingPrizeRef.current = null
      return { items, source: nextSource, groups: available, multiplier: nextMultiplier, target: nextTarget, chance: nextChance }
    } catch (requestError) {
      return { error: 'inventory' }
    }
  }

  const start = async () => {
    if (inFlightRef.current || isUpdateOperationBlocked('upgrader') || loading || error || !source || !target || chance === null) return
    inFlightRef.current = true
    const finishUpdateOperation = beginUpdateOperation('upgrader')
    setLoading('request')
    setOutcome(null)
    const previousIds = new Set(inventory.map(idOf))
    try {
      const result = await upgraderGameApi.upgrade(source.id, target.id)
      finishUpdateOperation()
      if (!mountedRef.current) return
      const win = result.win
      const actualChance = Number.isFinite(result.chance) && result.chance >= 0 && result.chance <= 100 ? result.chance : chance
      setSpin({ win, chance: actualChance, duration: SPIN_DURATION_MS, id: Date.now() })
      setLoading('spin')
      spinTimerRef.current = setTimeout(async () => {
        if (!mountedRef.current) return
        setSpin(null)
        setOutcome({ win, firstSuccess: result.firstSuccessUpgrade })
        setLoading('result')
        const [nextRound] = await Promise.all([
          prepareNextRound(win, previousIds, target, multiplier),
          new Promise((resolve) => { spinTimerRef.current = setTimeout(resolve, 3000) })
        ])
        if (!mountedRef.current) return
        if (nextRound.items) {
          setInventory(nextRound.items)
          setSource(nextRound.source ?? null)
          setGroups(nextRound.groups ?? {})
          setMultiplier(nextRound.multiplier ?? null)
          setTarget(nextRound.target ?? null)
          setChance(nextRound.chance ?? null)
        } else {
          setChance(null)
        }
        setError(nextRound.error ?? null)
        setLoading('reset')
        spinTimerRef.current = setTimeout(() => {
          if (!mountedRef.current) return
          setLoading(null)
          inFlightRef.current = false
        }, 1100)
      }, SPIN_DURATION_MS + 1100)
    } catch (requestError) {
      finishUpdateOperation(false)
      if (!mountedRef.current) return
      setSpin(null)
      await refreshInventory({ preferredSourceId: source.id, desiredMultiplier: multiplier })
      if (mountedRef.current) {
        setError('request')
        inFlightRef.current = false
      }
    }
  }

  const retry = () => {
    if (loading || inFlightRef.current) return
    if (pendingPrizeRef.current) refreshInventory(pendingPrizeRef.current)
    else refreshInventory({ preferredSourceId: source?.id, desiredMultiplier: multiplier })
  }

  return {
    inventory, source, groups, multiplier, target, chance, loading, error, outcome, spin,
    selectSource, selectMultiplier, selectTarget, start, retry
  }
}

export default useUpgraderGame
